// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xValidateOnSubmit } from "@west/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";

interface OwnProps {
    currentZipCode?: string;
    onUpdateHandler: (value?: string) => void;
    error?: string;
    onChangeHandler?: () => void;
}

interface EditZipCodeStyles {
    container?: InjectableCss;
    zipCodeEditField?: TextFieldPresentationProps;
    updateButton?: ButtonPresentationProps;
}

export const editZipCodeStyles: EditZipCodeStyles = {};

const styles = editZipCodeStyles;

const StyledForm = getStyledWrapper("form");

const formId = "zipCodeFormId";
const EditZipCode: FC<OwnProps> = ({
    currentZipCode,
    onUpdateHandler,
    error,
    onChangeHandler: onChangeHandlerProps,
}) => {
    const [zipCode, setZipCode] = useState(currentZipCode);
    const [isDisabled, setIsDisabled] = useState(false);

    useEffect(() => {
        const isDisabled = xValidateOnSubmit(formId, false)?.length > 0;
        setIsDisabled(isDisabled);
    }, [zipCode]);

    useEffect(() => {
        setZipCode(currentZipCode);
    }, [currentZipCode]);

    const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setZipCode(event.currentTarget.value);
        onChangeHandlerProps?.();
    };

    const onUpdateSaveHandler = (event: any) => {
        event.preventDefault();

        if (isDisabled) {
            return;
        }

        onUpdateHandler(zipCode);
    };

    return (
        <StyledForm {...styles.container} onSubmit={onUpdateSaveHandler}>
            <TextField
                type="postalCodeUS"
                value={zipCode}
                onChange={onChangeHandler}
                label={translate("ZIP Code")}
                placeholder={translate("Enter ZIP Code")}
                required={true}
                error={error}
                {...{ formId }}
                {...styles.zipCodeEditField}
            />
            <Button
                type="submit"
                disabled={isDisabled}
                {...styles.updateButton}
                aria-label={translate("Update Zip Code")}
            >
                {translate("Update")}
            </Button>
        </StyledForm>
    );
};

export default EditZipCode;
